// Bloque de medidas por producto — se inserta en el header de cada grupo de
// "Revisá tu catálogo" cuando el modo es `catalogo-muebles`. Ver ADR-583/584.
//
// Es la excepción a una decisión vieja: la pantalla de review del Catálogo IA
// NO tiene ningún campo de texto a propósito (ADR.md ~4903). Acá se justifica
// porque las medidas son el insumo del feature, no una preferencia estética.
//
// El componente es CONTROLADO: no guarda ni debouncea. El padre mantiene el
// estado y decide cuándo persistir — así una sola pantalla coordina todos los
// productos y no hay N timers sueltos.

import React, { useState } from 'react';
import { ChevronDown, Ruler } from 'lucide-react';
import {
  formatearMedida,
  parsearMedida,
  UNIDAD_POR_DEFECTO,
  type Medidas,
  type UnidadMedida,
} from '../../../lib/medidas';

const UNIDADES: UnidadMedida[] = ['cm', 'mm', 'm'];

/** Input de una dimensión. Mismo esqueleto que el `EditField` del sheet de
 *  export a Tiendanube, adaptado a la escala tipográfica de `pages/v2`. */
const CampoMedida: React.FC<{
  label: string;
  valor: number | null | undefined;
  onChange: (v: number | null) => void;
  /** Marca el campo en rojo suave cuando falta y es obligatorio. */
  falta?: boolean;
}> = ({ label, valor, onChange, falta }) => {
  // Estado local del texto para no pelear con lo que el user está tipeando:
  // si escribe "60," y normalizáramos en cada tecla, la coma desaparecería.
  const [texto, setTexto] = useState<string>(
    valor === null || valor === undefined ? '' : String(valor),
  );
  // Re-sincroniza si el valor cambia desde afuera (ej. llegó de Tienda Nube)
  // y el campo no está siendo editado.
  const [enfocado, setEnfocado] = useState(false);
  React.useEffect(() => {
    if (enfocado) return;
    setTexto(valor === null || valor === undefined ? '' : String(valor));
  }, [valor, enfocado]);

  return (
    <div className="min-w-0">
      <div className="text-[11px] text-neutral-500 mb-0.5">{label}</div>
      <input
        type="text"
        inputMode="decimal"
        value={texto}
        onFocus={() => setEnfocado(true)}
        onBlur={() => setEnfocado(false)}
        onChange={(e) => {
          setTexto(e.target.value);
          onChange(parsearMedida(e.target.value));
        }}
        placeholder="—"
        aria-label={label}
        className={`w-full h-11 px-3 rounded-xl bg-white border text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/30 outline-none transition-colors focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary ${
          falta ? 'border-error/60 bg-error-soft/30' : 'border-neutral-200'
        }`}
      />
    </div>
  );
};

export interface MedidasProductoBlockProps {
  medidas: Medidas | null;
  onChange: (next: Medidas) => void;
  /** true si alguna vista seleccionada exige medidas (hoy: la Infografía). */
  exigidas: boolean;
  /** Id del wrapper, para el scroll+shake al intentar generar sin completar. */
  anchorId?: string;
  /** Modo embebido: sin el marco externo (borde/fondo/col-span). Se usa para
   *  anidar el bloque dentro de la tira de producto del modo Infografía, donde
   *  el marco lo pone el contenedor. Default false = card suelta de siempre. */
  embedded?: boolean;
}

export const MedidasProductoBlock: React.FC<MedidasProductoBlockProps> = ({
  medidas,
  onChange,
  exigidas,
  anchorId,
  embedded = false,
}) => {
  const [expandido, setExpandido] = useState(false);
  const m: Medidas = medidas ?? { unidad: UNIDAD_POR_DEFECTO };

  const set = (parcial: Partial<Medidas>) => onChange({ ...m, ...parcial });

  // Falta = es obligatorio y no hay NINGUNA de las tres. No se marca campo por
  // campo: hay productos legítimamente sin profundidad (una alfombra, una
  // cerámica), y pedir las tres siempre sería mentirle al usuario.
  const sinNinguna =
    !m.alto && !m.ancho && !m.profundidad;
  const falta = exigidas && sinNinguna;

  const deTiendaNube = m.origen === 'tiendanube';

  const inner = (
    <>
      <div className="flex items-center gap-2 mb-2.5">
        <Ruler className="w-3.5 h-3.5 text-[#3C3C43]/50 shrink-0" />
        <span className="text-[13px] font-medium text-brand-black/80">
          Medidas
        </span>
        {deTiendaNube && (
          // El usuario tiene que saber que estos números los dedujo una máquina
          // del título/descripción de su tienda, no los escribió él.
          <span className="text-[11px] text-[#3C3C43]/55 truncate">
            · detectadas de tu tienda, revisalas
          </span>
        )}
        {/* Selector de unidad. Fila-botón compacta en vez de un select nativo,
            para no romper con el resto de la v2. */}
        <div className="ml-auto inline-flex items-center rounded-full bg-black/[0.05] p-0.5 shrink-0">
          {UNIDADES.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => set({ unidad: u })}
              className={`px-2.5 py-1 rounded-full text-[12px] font-medium transition-colors ${
                m.unidad === u
                  ? 'bg-white text-brand-black/90 shadow-sm'
                  : 'text-[#3C3C43]/60 hover:text-brand-black/80'
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <CampoMedida
          label="Alto"
          valor={m.alto}
          onChange={(v) => set({ alto: v })}
          falta={falta}
        />
        <CampoMedida
          label="Ancho"
          valor={m.ancho}
          onChange={(v) => set({ ancho: v })}
          falta={falta}
        />
        <CampoMedida
          label="Profundidad"
          valor={m.profundidad}
          onChange={(v) => set({ profundidad: v })}
          falta={falta}
        />
      </div>

      {expandido && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <CampoMedida
            label="Espesor"
            valor={m.espesor}
            onChange={(v) => set({ espesor: v })}
          />
          <CampoMedida
            label="Peso (kg)"
            valor={m.peso_kg}
            onChange={(v) => set({ peso_kg: v })}
          />
        </div>
      )}

      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          className="inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-opacity"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${expandido ? 'rotate-180' : ''}`}
          />
          {expandido ? 'Menos medidas' : 'Más medidas'}
        </button>
        {falta ? (
          <span className="text-[12px] text-error-text">
            La infografía necesita al menos una medida.
          </span>
        ) : (
          // Eco de lo cargado, para que el user confirme de un vistazo que no
          // se comió un cero.
          !sinNinguna && (
            <span className="text-[12px] text-[#3C3C43]/55 truncate">
              {[
                formatearMedida(m.alto, m.unidad),
                formatearMedida(m.ancho, m.unidad),
                formatearMedida(m.profundidad, m.unidad),
              ]
                .filter(Boolean)
                .join(' × ')}
            </span>
          )
        )}
      </div>
    </>
  );

  return embedded ? (
    <div id={anchorId} className="min-w-0">
      {inner}
    </div>
  ) : (
    <div
      id={anchorId}
      className={`col-span-full rounded-2xl border bg-white p-3.5 mb-1 transition-colors ${
        falta ? 'border-error/50' : 'border-neutral-200'
      }`}
    >
      {inner}
    </div>
  );
};

export default MedidasProductoBlock;
